import { isStringValue } from './validation/primitives';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date/timestamp in a human-readable format
 * @param date - Date object or string
 * @param options - Formatting options
 * @returns Formatted date string
 */
export function formatTimestamp(
  date: Date | string,
  options: {
    includeTime?: boolean;
    format?: 'full' | 'short' | 'compact';
  } = {}
): string {
  const { includeTime = false, format = 'full' } = options;
  const dateObj = isStringValue(date) ? new Date(date) : date;

  if (format === 'compact') {
    // Format: "Nov 23" (for recent transcriptions list)
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  if (format === 'short') {
    // Format: "Nov 23, 2025"
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Format: "November 23, 2025" or "November 23, 2025 at 07:28 AM"
  const datePart = dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (includeTime) {
    const timePart = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    return `${datePart} at ${timePart}`;
  }

  return datePart;
}

/** Format file size in bytes to human-readable string (e.g. "605.23 KB") */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/** Client-safe: language code to display name (no DB). Use for File Info etc.; server can use getLanguageName from language-display for full cache. */
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese',
  ru: 'Russian', ja: 'Japanese', ko: 'Korean', zh: 'Chinese', ar: 'Arabic', hi: 'Hindi',
  nl: 'Dutch', pl: 'Polish', tr: 'Turkish', vi: 'Vietnamese', th: 'Thai', id: 'Indonesian',
  eng: 'English',
};

export function getLanguageDisplayName(langCode: string | null | undefined): string {
  if (!langCode) return 'Auto-detected';
  const normalized = langCode.toLowerCase().replace(/^eng$/, 'en');

  return LANGUAGE_NAMES[normalized] ?? langCode.toUpperCase();
}
